import {
  DASHBOARD_METRIC_STATUS,
  DASHBOARD_WIDGET_TARGET_MODE,
  DASHBOARD_WIDGET_TYPE
} from './dashboardContract';

export const DASHBOARD_WIDGET_TYPE_OPTIONS = Object.freeze([
  {
    value: DASHBOARD_WIDGET_TYPE.NUMERIC,
    label: 'Numeric metric',
    description: 'Shows the latest approved value for one metric.'
  },
  {
    value: DASHBOARD_WIDGET_TYPE.PROGRESS,
    label: 'Progress tracker',
    description:
      'Shows one metric against a target with red, yellow, and green progress.'
  },
  {
    value: DASHBOARD_WIDGET_TYPE.PIE,
    label: 'Pie chart',
    description:
      'Compares the latest approved values for two to six compatible metrics.'
  },
  {
    value: DASHBOARD_WIDGET_TYPE.DOUGHNUT,
    label: 'Doughnut chart',
    description: 'Compares the latest approved values in a ring chart.'
  }
]);

export const DASHBOARD_WIDGET_TARGET_MODE_OPTIONS = Object.freeze([
  {
    value: DASHBOARD_WIDGET_TARGET_MODE.METRIC_MAXIMUM,
    label: 'Use the metric maximum'
  },
  { value: DASHBOARD_WIDGET_TARGET_MODE.CUSTOM, label: 'Use a custom target' }
]);

export const DASHBOARD_WIDGET_MAX_COUNT = 5;
export const DASHBOARD_WIDGET_MAX_METRICS = 6;

export const EMPTY_DASHBOARD_WIDGET_FORM = Object.freeze({
  name: '',
  description: '',
  widgetType: DASHBOARD_WIDGET_TYPE.NUMERIC,
  targetMode: '',
  targetValue: '',
  warningThreshold: '',
  successThreshold: '',
  showLegend: false,
  metrics: []
});

const widgetTypeValues = DASHBOARD_WIDGET_TYPE_OPTIONS.map(
  ({ value }) => value
);
const targetModeValues = DASHBOARD_WIDGET_TARGET_MODE_OPTIONS.map(
  ({ value }) => value
);
const normalizedText = (value) =>
  typeof value === 'string' ? value.trim() : '';
const finiteNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const isSingleMetricDashboardWidget = (widgetType) =>
  widgetType === DASHBOARD_WIDGET_TYPE.NUMERIC ||
  widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS;

export const canChangeDashboardWidgetType = (currentType, nextType) =>
  isSingleMetricDashboardWidget(currentType) ===
  isSingleMetricDashboardWidget(nextType);

export const sortDashboardWidgets = (widgets = []) =>
  [...widgets].sort(
    (left, right) =>
      left.displayOrder - right.displayOrder ||
      left.name.localeCompare(right.name)
  );

export const sortDashboardWidgetMetrics = (metrics = []) =>
  [...metrics].sort((left, right) => left.seriesOrder - right.seriesOrder);

export const getActiveDashboardWidgetMetrics = (metrics = []) =>
  [...metrics]
    .filter(
      (metric) => metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
    )
    .sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        left.label.localeCompare(right.label)
    );

export const dashboardWidgetToForm = (widget) => ({
  name: widget?.name ?? '',
  description: widget?.description ?? '',
  widgetType: widget?.widgetType ?? DASHBOARD_WIDGET_TYPE.NUMERIC,
  targetMode: widget?.targetMode ?? '',
  targetValue: widget?.targetValue ?? '',
  warningThreshold: widget?.warningThreshold ?? '',
  successThreshold: widget?.successThreshold ?? '',
  showLegend: widget?.showLegend ?? false,
  metrics: sortDashboardWidgetMetrics(widget?.metrics).map((metric) => ({
    metricDefinitionId: metric.metricDefinitionId,
    colour: metric.colour ?? ''
  }))
});

export const changeDashboardWidgetType = (form, widgetType) => {
  if (!widgetTypeValues.includes(widgetType)) return form;
  const singleMetric = isSingleMetricDashboardWidget(widgetType);
  const next = {
    ...form,
    widgetType,
    metrics: singleMetric ? form.metrics.slice(0, 1) : form.metrics
  };
  if (widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS) {
    return {
      ...next,
      targetMode:
        form.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS && form.targetMode
          ? form.targetMode
          : DASHBOARD_WIDGET_TARGET_MODE.METRIC_MAXIMUM,
      targetValue:
        form.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS
          ? form.targetValue
          : '',
      warningThreshold:
        form.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS
          ? form.warningThreshold
          : '50',
      successThreshold:
        form.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS
          ? form.successThreshold
          : '80',
      showLegend: false
    };
  }
  if (singleMetric) {
    return {
      ...next,
      targetMode: '',
      targetValue: '',
      warningThreshold: '',
      successThreshold: '',
      showLegend: false
    };
  }
  return {
    ...next,
    targetMode: '',
    targetValue: '',
    warningThreshold: '',
    successThreshold: '',
    showLegend:
      form.widgetType === DASHBOARD_WIDGET_TYPE.PIE ||
      form.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT
        ? Boolean(form.showLegend)
        : true
  };
};

export const toggleDashboardWidgetMetric = (
  metrics,
  metricDefinitionId,
  selected,
  widgetType
) => {
  const existing = metrics.some(
    (item) => item.metricDefinitionId === metricDefinitionId
  );
  if (selected && !existing) {
    const next = { metricDefinitionId, colour: '' };
    if (isSingleMetricDashboardWidget(widgetType)) return [next];
    if (metrics.length >= DASHBOARD_WIDGET_MAX_METRICS) return metrics;
    return [...metrics, next];
  }
  if (!selected && existing) {
    return metrics.filter(
      (item) => item.metricDefinitionId !== metricDefinitionId
    );
  }
  return metrics;
};

export const moveDashboardWidgetMetric = (
  metrics,
  metricDefinitionId,
  direction
) => {
  const ordered = [...metrics];
  const currentIndex = ordered.findIndex(
    (item) => item.metricDefinitionId === metricDefinitionId
  );
  const targetIndex = currentIndex + direction;
  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length)
    return ordered;
  const [item] = ordered.splice(currentIndex, 1);
  ordered.splice(targetIndex, 0, item);
  return ordered;
};

export const moveDashboardWidget = (widgets, dashboardWidgetId, direction) => {
  const ordered = sortDashboardWidgets(widgets);
  const currentIndex = ordered.findIndex(
    (widget) => widget.dashboardWidgetId === dashboardWidgetId
  );
  const targetIndex = currentIndex + direction;
  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) {
    return ordered.map((widget) => widget.dashboardWidgetId);
  }
  const [widget] = ordered.splice(currentIndex, 1);
  ordered.splice(targetIndex, 0, widget);
  return ordered.map((widget) => widget.dashboardWidgetId);
};

export const isDashboardWidgetMetricSelectable = (form, metric) => {
  const selected = form.metrics.some(
    (item) => item.metricDefinitionId === metric.metricDefinitionId
  );
  if (selected || isSingleMetricDashboardWidget(form.widgetType)) return true;
  return form.metrics.length < DASHBOARD_WIDGET_MAX_METRICS;
};

export const validateDashboardWidget = (form, activeMetrics = []) => {
  const errors = {};
  const name = normalizedText(form?.name);
  const description = normalizedText(form?.description);
  const widgetType = form?.widgetType;
  const activeMetricById = new Map(
    activeMetrics
      .filter(
        (metric) => metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
      )
      .map((metric) => [metric.metricDefinitionId, metric])
  );

  if (!name) errors.name = 'Widget name is required.';
  else if (name.length > 100)
    errors.name = 'Widget name cannot exceed 100 characters.';
  if (description.length > 500)
    errors.description = 'Description cannot exceed 500 characters.';
  if (!widgetTypeValues.includes(widgetType))
    errors.widgetType = 'Select a supported widget type.';

  const rawMetrics = Array.isArray(form?.metrics) ? form.metrics : [];
  if (isSingleMetricDashboardWidget(widgetType) && rawMetrics.length !== 1) {
    errors.metrics = 'Select exactly one active metric for this widget.';
  } else if (
    !isSingleMetricDashboardWidget(widgetType) &&
    (rawMetrics.length < 2 || rawMetrics.length > DASHBOARD_WIDGET_MAX_METRICS)
  ) {
    errors.metrics = `Select between two and ${DASHBOARD_WIDGET_MAX_METRICS} active metrics for this chart.`;
  }
  const ids = rawMetrics.map((metric) => metric.metricDefinitionId);
  if (new Set(ids).size !== ids.length)
    errors.metrics = 'A metric can appear only once in a widget.';

  const metrics = rawMetrics.map((item, index) => {
    const colour = normalizedText(item.colour);
    if (!activeMetricById.has(item.metricDefinitionId)) {
      errors.metrics =
        'Every widget metric must use an active dashboard metric.';
    }
    if (colour && !/^#[0-9A-Fa-f]{6}$/.test(colour)) {
      errors[`metrics.${index}.colour`] =
        'Colour must use a six-digit hex value such as #11A5A4.';
    }
    return {
      metricDefinitionId: item.metricDefinitionId,
      colour: colour ? colour.toUpperCase() : null
    };
  });

  let targetMode = null;
  let targetValue = null;
  let warningThreshold = null;
  let successThreshold = null;
  let showLegend = null;
  if (widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS) {
    targetMode = form?.targetMode;
    if (!targetModeValues.includes(targetMode)) {
      errors.targetMode = 'Choose how the progress target is set.';
    }
    if (targetMode === DASHBOARD_WIDGET_TARGET_MODE.CUSTOM) {
      targetValue = finiteNumber(form?.targetValue);
      if (targetValue === null || targetValue <= 0) {
        errors.targetValue = 'Custom target must be greater than zero.';
      }
    } else if (targetMode === DASHBOARD_WIDGET_TARGET_MODE.METRIC_MAXIMUM) {
      const selectedMetric = activeMetricById.get(ids[0]);
      const metricMaximum = finiteNumber(selectedMetric?.maximum);
      if (metricMaximum === null || metricMaximum <= 0) {
        errors.targetValue =
          'The selected metric needs a maximum greater than zero.';
      }
    }
    warningThreshold = finiteNumber(form?.warningThreshold);
    successThreshold = finiteNumber(form?.successThreshold);
    if (
      warningThreshold === null ||
      successThreshold === null ||
      warningThreshold < 0 ||
      successThreshold > 100 ||
      warningThreshold >= successThreshold
    ) {
      errors.warningThreshold =
        'Thresholds must satisfy 0 ≤ red-below < green-from ≤ 100.';
    }
  } else if (
    widgetType === DASHBOARD_WIDGET_TYPE.PIE ||
    widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT
  ) {
    showLegend = Boolean(form?.showLegend);
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    values: {
      configuration: {
        name,
        description: description || null,
        widgetType,
        targetMode,
        targetValue,
        warningThreshold,
        successThreshold,
        showLegend
      },
      metrics
    }
  };
};
